import React from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '../../styles/designSystem';
import { Phone, Calendar, Target, Globe, Shield,BarChart } from 'lucide-react';
import {  useState } from 'react';
import posthog from 'posthog-js';

import { DialogForm } from '../ui/DialogForm';
import { Helmet } from 'react-helmet-async';


const features = [
  {
    title: 'key_features.features.automated_call_handling.title',
    description: 'key_features.features.automated_call_handling.description',
    icon: Phone,
    color: 'violet',
    stats: ['key_features.features.automated_call_handling.stats.0', 'key_features.features.automated_call_handling.stats.1']
  },
  {
    title: 'key_features.features.appointment_scheduling.title',
    description: 'key_features.features.appointment_scheduling.description',
    icon: Calendar,
    color: 'indigo',
    stats: ['key_features.features.appointment_scheduling.stats.0', 'key_features.features.appointment_scheduling.stats.1']
  },
  {
    title: 'key_features.features.lead_generation.title',
    description: 'key_features.features.lead_generation.description',
    icon: Target,
    color: 'violet',
    stats: ['key_features.features.lead_generation.stats.0', 'key_features.features.lead_generation.stats.1']
  },
  {
    title: 'key_features.features.multi_language_support.title',
    description: 'key_features.features.multi_language_support.description',
    icon: Globe,
    color: 'indigo',
    stats: ['key_features.features.multi_language_support.stats.0', 'key_features.features.multi_language_support.stats.1']
  },
  {
    title: 'key_features.features.call_analytics.title',
    description: 'key_features.features.call_analytics.description',
    icon: BarChart, // Replace with the appropriate icon
    color: 'blue',
    stats: [
      'key_features.features.call_analytics.stats.0', // "Track conversion rates from calls to appointments."
      'key_features.features.call_analytics.stats.1', // "Identify peak call times and customer trends."

    ]
  },
  {
    title: 'key_features.features.gdpr_compliant.title',
    description: 'key_features.features.gdpr_compliant.description',
    icon: Shield,
    color: 'violet',
    stats: ['key_features.features.gdpr_compliant.stats.0', 'key_features.features.gdpr_compliant.stats.1']
  }
  
  
];

export const KeyFeatures = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [variant, setVariant] = useState('control');

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.target);
      const response = await fetch('https://formspree.io/f/xovvpnyb', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        setShowThankYou(true);
        e.target.reset();
        // Track successful demo request
        posthog.capture('demo_request', {
          variant: variant
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleButtonClick = (buttonType) => {
    posthog.capture('button_click', { button_type: buttonType});
  
    if (window.gtag) {
      window.gtag('event', 'button_click', {
        'event_category': 'engagement',
        'event_label': buttonType
      });
    }
  };
  return (
    <section className="py-6 relative overflow-hidden">
  
      <title>AI Automation | Sonitrix</title>
      <meta
        name="description"
        content="Explore the key features of Sonitrix AI solutions, including automated call handling, appointment scheduling, lead generation, and GDPR compliance."
      />
      <meta
        name="keywords"
        content="AI call automation features, automated call handling, appointment scheduling, lead generation, GDPR compliance, call analytics"
      />


      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-gray-100" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className={`${colors.text.heading} ${colors.primary.text} mb-6`}>
            {t('key_features.heading')}
          </h2>
          <p className={`${colors.text.body} text-lg max-w-2xl mx-auto`}>
            {t('key_features.description')}
          </p>
        </div>

        {/* Features Grid */}
{/* Features Grid */}
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
  {features.map((feature, index) => (
    <div key={index} className="relative group">
      {/* Gradient border effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-indigo-500/20 
           rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative h-full bg-white rounded-xl p-6 shadow-lg hover:shadow-xl 
           transition-all duration-300 border border-gray-100 flex flex-col"> {/* Added flex flex-col */}
        {/* Icon */}
        <div className={`p-3 rounded-lg bg-${feature.color}-500/10 w-fit mb-4`}>
          <feature.icon className={`w-6 h-6 text-${feature.color}-600`} />
        </div>
        
        {/* Content */}
        <div className="flex-grow"> {/* Added flex-grow to push the learn more link to bottom */}
          <h3 className="text-xl font-semibold mb-3">{t(feature.title)}</h3>
          <p className={`${colors.text.body} mb-4`}>{t(feature.description)}</p>
          
          {/* Stats */}
          <div className="space-y-2 mb-4">
            {feature.stats.map((stat, idx) => (
              <div key={idx} className="flex items-center text-sm text-gray-600">
                <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mr-2" />
                {t(stat)}
              </div>
            ))}
          </div>
        </div>
        

      </div>
    </div>
  ))}
</div>


        {/* Bottom CTA */}
        <div className="text-center">
          <div className={`${colors.container.base} inline-block p-6`}>
            <p className="font-semibold mb-2">{t('key_features.cta.title')}</p>
            <p className={`${colors.text.subtitle} mb-4`}>
              {t('key_features.cta.subtitle')}
            </p>
            <div className="mt-8 text-center flex justify-center gap-4">
          <DialogForm
            triggerText={t('App.book_demo')}
            title={t('App.book_demo')}
            description="Schedule a personalized demo with our team"
            onSubmit={handleDemoSubmit}
            onClick={() => handleButtonClick('demo_button')}

          />
              <DialogForm
                triggerText={t('App.get_free_trial')}
                title={t('App.get_free_trial')}
                description="Start your free trial today"
                onSubmit={handleDemoSubmit}
                onClick={() => handleButtonClick('trial_button')}

                buttonStyle="black"
              />
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KeyFeatures;
